/**
 * UserMenu - Store-connected container that wires UserProfileMenu to portal state.
 * Reads user data from UserContextProvider store; delegates rendering to UserProfileMenu.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import {
  useDisplayName,
  useCsrfToken,
  useTenantNetworkId,
} from '../UserContextProvider/UserContextProvider.store';
import { navigatePortalInternal, startPortalNavigation } from '../PortalLayout/PortalLayout.store';
import { logger, mediator } from '@/application/core';
import { LogoutCommand } from '@/application/commands/auth';
import { UserProfileMenu } from '../UserProfileMenu';

export interface UserMenuProps {
  /** Optional className for the trigger button */
  className?: string;
}

export function UserMenu({ className }: UserMenuProps) {
  const { t } = useTranslation(NS.COMMON);
  const rawDisplayName = useDisplayName();
  const csrfToken = useCsrfToken();
  const tenantNetworkId = useTenantNetworkId();

  const displayName = rawDisplayName || t('UserMenu_DefaultDisplayName', { defaultValue: 'User' });

  const redirectToHome = () => {
    const redirected = navigatePortalInternal('/', {
      allowWhileNavigating: true,
      allowSameUrl: true,
    });
    if (!redirected) {
      window.location.href = '/';
    }
  };

  const handleLogout = async () => {
    if (!startPortalNavigation()) return;
    try {
      await mediator.send(new LogoutCommand(csrfToken));
    } catch (error) {
      logger.error('UserMenu', 'Logout error:', error);
    } finally {
      redirectToHome();
    }
  };

  const handleProfile = () => navigatePortalInternal('/user-profile');
  const handleBillingInfo = () => navigatePortalInternal('/billing-information');

  return (
    <UserProfileMenu
      displayName={displayName}
      showBilling={tenantNetworkId === 50}
      className={className}
      onProfile={handleProfile}
      onBilling={handleBillingInfo}
      onLogout={handleLogout}
    />
  );
}
